<template>
    <tr v-if="isFightQueueBreak" @mouseover="fightHover" class="break-row">
        <td>
            {{ index + 1 }}
        </td>
        <td class="break-content">-- {{ translate("admin_fights_manage.break") }} --</td>
        <td>-- {{ translate("admin_fights_manage.break") }} --</td>
        <td>-- {{ translate("admin_fights_manage.break") }} --</td>
        <td>-- {{ translate("admin_fights_manage.break") }} --</td>
        <slot name="afterCells" />
    </tr>
    <tr v-else @mouseover="fightHover" class="fight-row" :class="getClassForFightQueueStatus">
        <td>
            <input v-if="!!selectionMode" v-model="fightsStore.selectedFightsToMove[fight.id]" type="checkbox" />
            <template v-else>
                <span v-if="!fight.fightNumber">{{ index + 1 }} </span>
                <span v-else :title="`${fight.id}`">{{ fight.fightNumber }} </span>
            </template>
        </td>
        <td>
            <a v-if="selectionMode" class="p-1" :class="getHighlightedClass" @click="selectCategory(fight)">
                {{ fight.category }}
            </a>
            <span v-else class="p-1" :class="getHighlightedClass">{{ fight.category }}</span>

            <BracketTypeBadge :fight="fight" />
        </td>

        <td>
            <FightRoundName :round-name="fight.roundName" :bracket-level="fight.bracketLevel" />
        </td>

        <td>
            <FightCompetitorName :competitor="fight.firstCompetitor" :duplicated-pair="showDuplicatedPair && fight.duplicatedPair" />
            <DuplicatedPairWarning v-if="showDuplicatedPair" :fight="fight" />

            <br v-if="!fight.firstCompetitor.status.verified" />
            <CompetitorStatus base-class="tag" :status="fight.firstCompetitor.status" />

            <br />

            <small v-if="fight.firstCompetitor.academy">{{ fight.firstCompetitor.academy }}</small>
        </td>

        <td>
            <FightCompetitorName :competitor="fight.secondCompetitor" :duplicated-pair="showDuplicatedPair && fight.duplicatedPair" />
            <DuplicatedPairWarning v-if="showDuplicatedPair" :fight="fight" />

            <br v-if="!fight.secondCompetitor.status.verified" />
            <CompetitorStatus base-class="tag" :status="fight.secondCompetitor.status" />

            <br />

            <small v-if="fight.secondCompetitor.academy">{{ fight.secondCompetitor.academy }}</small>
        </td>

        <slot name="afterCells" />
    </tr>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import FightCompetitorName from "./FightCompetitorName.vue";
    import CompetitorStatus from "../Common/CompetitorStatus.vue";
    import BracketTypeBadge from "../Common/BracketTypeBadge.vue";
    import FightRoundName from "../Common/FightRoundName.vue";
    import { translate } from "../Common/translations";
    import { FightQueueStatus, isQueueBreak, OrganizerComplexFight } from "../Common/Fights/types";
    import DuplicatedPairWarning from "./DuplicatedPairWarning.vue";
    import { useFightSelectionStore } from "./fightsStore";

    const STATUS_AWAITING_COMPETITORS = 1;
    const STATUS_FIGHT_IN_PROGRESS = 2;

    const fightsStore = useFightSelectionStore();

    const props = defineProps<{
        fight: OrganizerComplexFight;
        index: number;
        selectionMode?: boolean;
        highlightParameterId?: number;
        fightQueueStatus?: FightQueueStatus | null;
        showDuplicatedPair: boolean;
    }>();

    const emit = defineEmits(["fightHover", "selectCategory"]);

    const getHighlightedClass = computed(() => {
        return props.highlightParameterId === props.fight.parameterId ? "highlight-category" : "";
    });

    const getClassForFightQueueStatus = computed(() => {
        if (!props.fightQueueStatus) {
            return "";
        }

        if (props.fight.id !== props.fightQueueStatus.fightId) {
            return "";
        }

        if (props.fightQueueStatus.status === STATUS_AWAITING_COMPETITORS) {
            return "fight-active fight-awaiting-competitors";
        }
        if (props.fightQueueStatus.status === STATUS_FIGHT_IN_PROGRESS) {
            return "fight-active fight-in-progress";
        }
        return "";
    });

    const isFightQueueBreak = computed(() => {
        return isQueueBreak(props.fight);
    });

    function fightHover() {
        emit("fightHover", props.fight.parameterId);
    }

    function selectCategory(fight: OrganizerComplexFight) {
        emit("selectCategory", fight);
    }
</script>

<style scoped>
    .highlight-category {
        color: white;
        background-color: rgba(49, 142, 255, 0.35);
    }

    .table .fight-awaiting-competitors {
        background-color: rgba(255, 167, 5, 0.4);
    }

    .table .fight-in-progress {
        background-color: rgba(45, 255, 80, 0.2);
    }

    .break-row {
        background: repeating-linear-gradient(-45deg, #353a41, #353a41 10px, #2e3338 10px, #2e3338 20px);
    }

    .break-content {
        text-transform: uppercase;
    }

    .break-row:hover {
        background: repeating-linear-gradient(-45deg, #272b30, #272b30 10px, #2e3338 10px, #2e3338 20px);
    }
</style>
